<script setup>
import { userAuthState } from '@/@core/auth'
import { use2FA } from '@/composables/use2FA'
import authV1BottomShape from '@images/svg/auth-v1-bottom-shape.svg?raw'
import authV1TopShape from '@images/svg/auth-v1-top-shape.svg?raw'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { useRouter } from 'vue-router'
import { VOtpInput } from 'vuetify/labs/VOtpInput'

definePage({ meta: { layout: 'blank', unauthenticatedOnly: true } })

const router = useRouter()
const authStore = userAuthState()

const {
  twoFactorState,
  verifyCode,
  resendCode,
  getMethodInfo,
} = use2FA()

// Component state
const otp = ref('')
const recoveryCode = ref('')
const isLoading = ref(false)
const errorMessage = ref('')
const isUsingRecoveryCode = ref(false)
const resendCooldown = ref(0)
const canResend = ref(true)
const isAccountLocked = ref(false)
const lockoutTimeRemaining = ref(0)

// Check if we're in login flow (have temporary token)
const isLoginFlow = computed(() => {
  return !!authStore.tempToken
})

// Get current method info
const currentMethodInfo = computed(() => {
  const method = twoFactorState.verification.method || 'GOOGLE'

  return getMethodInfo(method)
})

// Get masked target (phone/email)
const maskedTarget = computed(() => {
  return twoFactorState.verification.maskedTarget || '******1234'
})

// Check if method supports resending
const canResendMethod = computed(() => {
  const method = twoFactorState.verification.method

  return ['SMS', 'Mail', 'WhatsApp'].includes(method)
})

// Main verification function
const onFinish = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const codeToVerify = isUsingRecoveryCode.value ? recoveryCode.value : otp.value
    const recoveryCodeValue = isUsingRecoveryCode.value ? recoveryCode.value : null

    let result

    if (isLoginFlow.value) {
      // Use auth store for login 2FA verification (uses temporary token)
      result = await authStore.verify2FA(codeToVerify, recoveryCodeValue)
    } else {
      // Use 2FA composable for regular 2FA verification (during settings)
      result = await verifyCode(codeToVerify, recoveryCodeValue)
    }

    if (result.status) {
      // Successful verification, redirect to dashboard
      router.push('/app/panel')
    } else {
      console.error('2FA verification failed', result)
      errorMessage.value = result.message || 'Invalid verification code. Please try again.'

      // Check for lockout information
      if (result.is_locked) {
        isAccountLocked.value = true
        lockoutTimeRemaining.value = result.lockout_time_remaining || 0
      }
    }
  } catch (error) {
    errorMessage.value = error.message || 'Invalid verification code. Please try again.'

    // Check if error contains lockout info
    if (error.message && error.message.includes('locked')) {
      isAccountLocked.value = true
    }
  } finally {
    isLoading.value = false

    // Reset form on error
    if (isUsingRecoveryCode.value) {
      recoveryCode.value = ''
    } else {
      otp.value = ''
    }
  }
}

// Resend OTP
const handleResendOTP = async () => {
  if (!canResend.value || !canResendMethod.value) return

  try {
    let result

    if (isLoginFlow.value) {
      // Use auth store for login 2FA resend (uses temporary token)
      result = await authStore.resend2FA()
    } else {
      // Use 2FA composable for regular 2FA resend (during settings)
      result = await resendCode()
    }

    if (result.status) {
      startResendCooldown()
      errorMessage.value = ''

      // Show success message
      const methodName = currentMethodInfo.value.title.toLowerCase()

      console.log(`New code sent to your ${methodName}`)
    } else {
      errorMessage.value = result.message || 'Failed to resend code'
    }
  } catch (error) {
    errorMessage.value = error.message || 'Failed to resend code'
  }
}

// Start cooldown timer for resend
const startResendCooldown = (seconds = 60) => {
  canResend.value = false
  resendCooldown.value = seconds

  const timer = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0) {
      clearInterval(timer)
      canResend.value = true
    }
  }, 1000)
}

// Toggle between OTP and recovery code
const toggleRecoveryCode = () => {
  isUsingRecoveryCode.value = !isUsingRecoveryCode.value
  errorMessage.value = ''
  otp.value = ''
  recoveryCode.value = ''
}

// Initialize on mount
onMounted(() => {
  // Check if we're in a valid 2FA flow
  const hasVerificationRequired = twoFactorState.verification.isRequired
  const hasTemporaryToken = !!authStore.tempToken

  // If neither login flow nor settings flow is active, redirect to login
  if (!hasVerificationRequired && !hasTemporaryToken) {
    router.push('/app/login')
  }
})
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <div class="position-relative my-sm-16">
      <!-- 👉 Top shape -->
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1TopShape })"
        class="text-primary auth-v1-top-shape d-none d-sm-block"
      />

      <!-- 👉 Bottom shape -->
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1BottomShape })"
        class="text-primary auth-v1-bottom-shape d-none d-sm-block"
      />

      <!-- 👉 Auth card -->
      <VCard
        class="auth-card pa-4"
        max-width="448"
      >
        <VCardItem class="justify-center">
          <template #prepend>
            <div class="d-flex">
              <VNodeRenderer :nodes="themeConfig.app.client.logo" />
            </div>
          </template>
        </VCardItem>

        <VCardText class="pt-2">
          <div class="text-center mb-4">
            <VIcon
              :icon="currentMethodInfo.icon"
              :color="currentMethodInfo.color"
              size="48"
              class="mb-3"
            />
            <h4 class="text-h4 mb-2">
              Two Step Verification
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              {{ currentMethodInfo.title === 'Authenticator App'
                ? 'Enter the 6-digit code from your authenticator app'
                : `We sent a verification code to ${maskedTarget}`
              }}
            </p>
          </div>

          <h6 class="text-h6 mb-4 text-center">
            {{ isUsingRecoveryCode ? 'Enter Recovery Code' : `Type your ${currentMethodInfo.title === 'Authenticator App' ? '6' : '6'} digit security code` }}
          </h6>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="onFinish">
            <VRow>
              <!-- Recovery Code Input -->
              <VCol
                v-if="isUsingRecoveryCode"
                cols="12"
              >
                <VTextField
                  v-model="recoveryCode"
                  label="Recovery Code"
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  prepend-inner-icon="tabler-key"
                  :disabled="isLoading || isAccountLocked"
                  :error="!!errorMessage"
                  autocomplete="off"
                  variant="outlined"
                  class="text-center"
                />
              </VCol>

              <!-- OTP Input -->
              <VCol
                v-else
                cols="12"
              >
                <VOtpInput
                  v-model="otp"
                  :disabled="isLoading || isAccountLocked"
                  type="number"
                  class="pa-0 justify-center"
                  @finish="onFinish"
                />
              </VCol>

              <!-- Error message -->
              <VCol
                v-if="errorMessage"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                  class="mb-0"
                >
                  <VAlertTitle>
                    {{ errorMessage.includes('locked') ? 'Account Locked' : 'Verification Failed' }}
                  </VAlertTitle>
                  {{ errorMessage }}
                </VAlert>
              </VCol>

              <!-- Toggle Recovery Code -->
              <VCol cols="12">
                <div class="text-center mb-3">
                  <VBtn
                    variant="text"
                    size="small"
                    color="primary"
                    @click="toggleRecoveryCode"
                  >
                    <VIcon
                      :icon="isUsingRecoveryCode ? 'tabler-smartphone' : 'tabler-key'"
                      class="me-2"
                    />
                    {{ isUsingRecoveryCode ? 'Use verification code instead' : 'Use recovery code instead' }}
                  </VBtn>
                </div>
              </VCol>

              <!-- Verify button -->
              <VCol cols="12">
                <VBtn
                  :loading="isLoading"
                  :disabled="isLoading || isAccountLocked || (isUsingRecoveryCode ? !recoveryCode.trim() : otp.length !== 6)"
                  block
                  type="submit"
                  color="primary"
                  size="large"
                >
                  {{ isAccountLocked ? 'Account Locked' : 'Verify my account' }}
                </VBtn>
              </VCol>

              <!-- Resend option for OTP methods -->
              <VCol
                v-if="!isUsingRecoveryCode && canResendMethod && !isAccountLocked"
                cols="12"
              >
                <div class="text-center">
                  <div class="d-flex justify-center align-center flex-wrap">
                    <span class="text-body-2 me-2">Didn't get the code?</span>
                    <VBtn
                      v-if="canResend"
                      variant="text"
                      size="small"
                      color="primary"
                      @click="handleResendOTP"
                    >
                      Resend Code
                    </VBtn>
                    <span
                      v-else
                      class="text-body-2 text-medium-emphasis"
                    >
                      Resend available in {{ resendCooldown }}s
                    </span>
                  </div>
                </div>
              </VCol>

              <!-- Back to login -->
              <VCol cols="12">
                <div class="text-center">
                  <VBtn
                    variant="text"
                    color="primary"
                    to="/app/login"
                  >
                    <VIcon
                      icon="tabler-arrow-left"
                      class="me-2"
                    />
                    Back to Login
                  </VBtn>
                </div>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </div>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
